import React, { useEffect, useMemo, useState } from "react";
import "./Inventory.css";
import InventoryEntry from "../InventoryEntry/InventoryEntry";
import DropdownSelect from "../FilterSelect/FilterSelect";
import PrimaryFilter from "../PrimaryFilter/PrimaryFilter";
import { IReducer } from "../../state/reducer";
import { BrickInfoTotal } from "../../state/BrickInfoTotal/BrickInfoTotal";
import { BrickInfo } from "../../state/BrickInfoTotal/BrickInfo";
import { RowMode } from "../RowMode/RowMode";
import InventoryRow from "../InventoryRow/InventoryRow";
import { useCanvas } from "../GlobalCanvasManager/CanvasContext";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragMoveEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

interface InventoryProps extends IReducer {
  showInventorySettings: CallableFunction;
}

const Inventory: React.FC<InventoryProps> = ({
  state,
  dispatch,
  showInventorySettings,
}) => {
  const [visibleBricks, setVisibleBricks] = useState<BrickInfo[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [activeSearch, setActiveSearch] = useState<string>("");
  const { syncViewports, triggerSync } = useCanvas();

  // Drag and drop sensors (8px activation distance to prevent accidental drags)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
  );

  // Handle drag move to update canvas positions during drag
  const handleDragMove = (_event: DragMoveEvent) => {
    if (!state.editModeInventory) return;
    // Trigger sync on next frame while dragging to keep 3D models aligned
    triggerSync();
  };

  // Handle drag end to reorder bricks
  const handleDragEnd = (event: DragEndEvent) => {
    if (!state.editModeInventory) return; // Only allow reorder in Edit mode

    const { active, over } = event;
    if (!over || active.id === over.id) {
      // Even if no reorder happened, wait for CSS transition to complete before syncing
      // dnd-kit applies a transition to snap element back to original position
      setTimeout(() => {
        triggerSync();
      }, 200); // Wait for transition to complete (dnd-kit default is ~250ms)
      return;
    }

    const oldIndex = visibleBricks.findIndex((b) => b.id === active.id);
    const newIndex = visibleBricks.findIndex((b) => b.id === over.id);

    const reordered = arrayMove(visibleBricks, oldIndex, newIndex);
    const newOrder = reordered.map((b) => b.id);

    dispatch({ type: "SET_INVENTORY_ORDER", inventoryOrder: newOrder });

    // Wait for dnd-kit CSS transition to complete before syncing viewports
    // The element needs to finish animating to its final position before we read getBoundingClientRect()
    setTimeout(() => {
      triggerSync();
    }, 200); // Wait for transition to complete (dnd-kit default is ~250ms)
  };

  // Calculate total discovered bricks (before search/filters)
  const totalDiscoveredBricks = useMemo(() => {
    let count = 0;
    state.bricks.forEach((brickEntry) => {
      if (!Number.isNaN(brickEntry.amount)) {
        count++;
      }
    });
    return count;
  }, [state.bricks]);

  // Sync viewports when row mode toggles to update model positions immediately
  useEffect(() => {
    // Sync immediately (synchronously) to update positions before next render
    syncViewports();

    // Then sync again after React updates layout, in case there were changes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncViewports();
      });
    });
  }, [state.useInventoryRows, syncViewports]);

  // Compute visible bricks with custom ordering
  const computedVisibleBricks = useMemo(() => {
    let filteredBricks: BrickInfo[] = [];

    state.bricks.forEach((brickEntry, key) => {
      const brickInfo = BrickInfoTotal.get(key);

      // Only show discovered bricks that are not hidden by user
      if (
        !Number.isNaN(brickEntry.amount) &&
        brickInfo &&
        !brickEntry.userHidden
      ) {
        filteredBricks.push(brickInfo);
      }
    });

    // Step 1: Apply search filter if activeSearch is not empty
    if (activeSearch.trim() !== "") {
      filteredBricks = filteredBricks.filter((brick) =>
        brick.name.toLowerCase().includes(activeSearch.toLowerCase()),
      );
    }

    // Step 2: Apply type filters if any exist
    if (appliedFilters.length > 0) {
      filteredBricks = filteredBricks.filter((brick) =>
        appliedFilters.includes(brick.type),
      );
    }

    // Step 3: Apply custom ordering if inventoryOrder is not empty
    if (state.inventoryOrder.length > 0) {
      const ordered: BrickInfo[] = [];
      const remaining = new Set(filteredBricks.map((b) => b.id));

      // Add bricks in custom order
      state.inventoryOrder.forEach((id) => {
        const brick = filteredBricks.find((b) => b.id === id);
        if (brick) {
          ordered.push(brick);
          remaining.delete(id);
        }
      });

      // Append new bricks not in order (e.g., newly discovered)
      filteredBricks.forEach((brick) => {
        if (remaining.has(brick.id)) {
          ordered.push(brick);
        }
      });

      return ordered;
    }

    return filteredBricks;
  }, [appliedFilters, activeSearch, state.bricks, state.inventoryOrder]);

  // Update local state when computed bricks change
  useEffect(() => {
    setVisibleBricks(computedVisibleBricks);
    // Viewports will auto-sync on next frame via GlobalCanvasManager
    // No manual sync needed - the RAF throttling handles it
  }, [computedVisibleBricks]);

  // Viewports will auto-sync when row mode toggles
  // GlobalCanvasManager's scroll listener on .invRedux/.rowActions will trigger sync

  // Memoized filter options (if needed)
  const filterOptions = useMemo(
    () => ["Brick", "Plate", "Minifig", "Bionicle", "Animal"],
    [],
  );

  return (
    <div className="moduleContainer">
      <div className="moduleSpacer"></div>
      <div className="inventoryHeader">
        <div className="headerContent">
          <div className="inventoryHeaderTitle">
            {state.editModeInventory ? "Reorder Inventory" : "Inventory"}
          </div>
        </div>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={visibleBricks.map((b) => b.id)}
          strategy={
            state.useInventoryRows
              ? verticalListSortingStrategy
              : rectSortingStrategy
          }
        >
          {state.useInventoryRows ? (
            <div className="rowActions">
              {visibleBricks.map((brick) => (
                <InventoryRow
                  key={brick.id}
                  state={state}
                  dispatch={dispatch}
                  brickId={brick.id}
                />
              ))}
            </div>
          ) : (
            <div className="invRedux">
              {visibleBricks.map((brick) => (
                <InventoryEntry
                  key={brick.id}
                  state={state}
                  dispatch={dispatch}
                  brickId={brick.id}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
      <div
        className={`inventoryFooter ${state.editModeInventory ? "editModeActive" : ""}`}
      >
        <div className="inventoryHeaderOptions">
          <div className="compendiumFilters">
            <div className="moduleTotal">
              Total:{" "}
              <span className="moduleTotalAmt">{totalDiscoveredBricks}</span>
            </div>
            <DropdownSelect
              placeholder="Filters"
              options={filterOptions}
              setAppliedFilters={setAppliedFilters}
              disabled={state.editModeInventory}
            />
            <PrimaryFilter
              placeholder="Search"
              setActiveSearch={setActiveSearch}
              disabled={state.editModeInventory}
            />
            <RowMode
              state={state}
              dispatch={dispatch}
              rowType="TOGGLE_USE_INVENTORY_ROWS"
              disabled={state.editModeInventory}
            />
            <div
              className={`editModeButton ${state.editModeInventory ? "saveMode" : ""}`}
              onClick={() => dispatch({ type: "TOGGLE_EDIT_MODE_INVENTORY" })}
            >
              {state.editModeInventory ? "Save" : "Edit"}
            </div>
            <div
              className={`actionSettings ${state.editModeInventory ? "disabled" : ""}`}
              onClick={
                state.editModeInventory
                  ? undefined
                  : () => showInventorySettings(true)
              }
            >
              Settings
            </div>
          </div>
        </div>
      </div>
      <div className="moduleSpacer"></div>
    </div>
  );
};
export default Inventory;
